import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';

const PAYMENT_OPTS  = ['', 'Net 15', 'Net 30', 'Net 45', 'Net 60', 'Net 90', 'COD', 'Advance'];
const CURRENCY_OPTS = ['INR', 'USD', 'EUR', 'JPY', 'GBP', 'CNY'];
const STATUS_OPTS   = ['Active', 'Inactive', 'Advanced'];
const INDUSTRY_OPTS = ['', 'Manufacturing', 'Trading', 'Services', 'Construction', 'Chemicals', 'Automotive', 'Electronics', 'Textiles', 'Food & Beverage', 'Pharmaceuticals', 'Other'];
const DUNNING_OPTS  = ['', 'Standard', 'Lenient', 'Strict'];
const SHIPPING_OPTS = ['', 'Air', 'Road', 'Rail', 'Sea', 'Courier'];
const LANGUAGE_OPTS = ['', 'English', 'Hindi', 'Tamil', 'Telugu', 'Kannada', 'Marathi', 'Bengali', 'Gujarati'];
const BUSINESS_TYPE_OPTS = ['', 'Company', 'Individual', 'Partnership', 'Government', 'Other'];

const TABS = [
  { key: 'general',     label: 'General' },
  { key: 'payTerms',    label: 'Payment Terms' },
  { key: 'payRun',      label: 'Payment Run' },
  { key: 'accounting',  label: 'Accounting' },
  { key: 'remarks',     label: 'Remarks' },
];

const inp = `w-full h-[30px] px-2 text-xs border border-sap-border rounded bg-white
             focus:outline-none focus:ring-1 focus:ring-sap-blue/40 focus:border-sap-blue`;
const sel = `${inp} appearance-none`;
const lbl = `block text-[11px] font-medium text-sap-textLight mb-0.5 whitespace-nowrap`;

export default function VendorDetail() {
  const { id } = useParams();
  const isNew  = id === 'new';
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [tab, setTab]         = useState('general');

  const emptyForm = {
    vendor_code: '', vendor_name: '', sap_card_code: '', foreign_name: '', group_code: '',
    currency: 'INR', federal_tax_id: '', contact_person: '',
    phone1: '', phone2: '', mobile: '', fax: '', email: '', website: '',
    shipping_type: '', password: '', factoring_ind: '', bp_project: '',
    bp_channel_code: '', industry: '', type_of_business: 'Company',
    territory: '', language: '', alias_name: '', gln: '',
    id_no2: '', unified_tax_id: '', remarks_general: '',
    status: 'Active', block_marketing: false,
    // Payment Terms
    payment_terms: '', interest_on_arrears: 0, price_list: '',
    total_discount: 0, credit_limit: 0, max_commitment: 0,
    dunning_term: '', auto_posting: 'No',
    blanket_agreement: '', eff_discount_grp: 'Lowest Discount', eff_price: 'Default Priority',
    credit_card_type: '', credit_card_no: '', cc_expiration: '', cc_id_number: '',
    average_delay: '', priority: '', default_iban: '', holidays: '',
    allow_partial_delivery: true, allow_partial_per_row: true,
    no_discount_groups: false, endorsable_checks: true, accepts_endorsed: false,
    // Bank details
    bank_country: '', bank_name: '', bank_code: '', bank_account: '',
    bic_swift: '', bank_account_name: '', bank_branch: '', bank_ctrl_id: '',
    bank_iban: '', bank_mandate_id: '', bank_sign_date: '',
    // Payment Run
    house_bank_country: '', house_bank: '', house_bank_account: '',
    house_bank_branch: '', house_bank_iban: '', house_bic_swift: '',
    house_ctrl_no: '', ref_details: '',
    payment_block: false, single_payment: false, collection_auth: false,
    bank_charges_code: '',
    // Accounting
    consolidating_bp: '', payment_consolidation: false, delivery_consolidation: false,
    acct_receivable: '', dp_clearing_acct: '', dp_interim_acct: '',
    planning_group: '', affiliate: false,
    // Remarks
    remarks: '',
  };

  const [form, setForm] = useState(emptyForm);

  const f = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  useEffect(() => {
    if (isNew) {
      (async () => {
        try {
          const n = await api.get('/vendors/next-code');
          setForm(prev => ({ ...prev, vendor_code: n.next }));
        } catch {}
      })();
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const d = await api.get(`/vendors/${id}`);
        const mapped = {};
        for (const key of Object.keys(emptyForm)) {
          if (typeof emptyForm[key] === 'boolean') {
            mapped[key] = d[key] === 1 || d[key] === true;
          } else if (typeof emptyForm[key] === 'number') {
            mapped[key] = d[key] ?? emptyForm[key];
          } else {
            mapped[key] = d[key] || emptyForm[key];
          }
        }
        setForm(mapped);
      } catch (e) {
        setError('Failed to load vendor');
      } finally { setLoading(false); }
    })();
  }, [id, isNew]);

  const handleSave = async () => {
    if (!form.vendor_code.trim() || !form.vendor_name.trim()) {
      setError('Vendor code and name are required.');
      return;
    }
    setSaving(true); setError('');
    try {
      if (isNew) {
        const r = await api.post('/vendors', form);
        navigate(`/vendor/${r.vendor_id}`, { replace: true });
      } else {
        await api.put(`/vendors/${id}`, form);
      }
      setError('');
      if (!isNew) {
        const d = await api.get(`/vendors/${id}`);
        const mapped = {};
        for (const key of Object.keys(emptyForm)) {
          if (typeof emptyForm[key] === 'boolean') mapped[key] = d[key] === 1 || d[key] === true;
          else if (typeof emptyForm[key] === 'number') mapped[key] = d[key] ?? emptyForm[key];
          else mapped[key] = d[key] || emptyForm[key];
        }
        setForm(mapped);
      }
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin h-6 w-6 border-2 border-sap-blue border-t-transparent rounded-full" />
    </div>
  );

  /* ── Field helper ── */
  const Field = ({ label, children, span = 1 }) => (
    <div className={span === 2 ? 'col-span-2' : ''}>
      <label className={lbl}>{label}</label>
      {children}
    </div>
  );

  /* ══════════════════════════════════════════════════
     TAB: General
     ══════════════════════════════════════════════════ */
  const GeneralTab = () => (
    <div className="space-y-4">
      {/* Contact */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2">
        <Field label="Tel 1">
          <input className={inp} value={form.phone1} onChange={e => f('phone1', e.target.value)} />
        </Field>
        <Field label="Tel 2">
          <input className={inp} value={form.phone2} onChange={e => f('phone2', e.target.value)} />
        </Field>
        <Field label="Mobile Phone">
          <input className={inp} value={form.mobile} onChange={e => f('mobile', e.target.value)} />
        </Field>
        <Field label="Fax">
          <input className={inp} value={form.fax} onChange={e => f('fax', e.target.value)} />
        </Field>

        <Field label="E-Mail" span={2}>
          <input className={inp} value={form.email} onChange={e => f('email', e.target.value)} type="email" />
        </Field>
        <Field label="Web Site" span={2}>
          <input className={inp} value={form.website} onChange={e => f('website', e.target.value)} />
        </Field>

        <Field label="Shipping Type">
          <select className={sel} value={form.shipping_type} onChange={e => f('shipping_type', e.target.value)}>
            {SHIPPING_OPTS.map(s => <option key={s} value={s}>{s || '— Select —'}</option>)}
          </select>
        </Field>
        <Field label="Password">
          <input className={inp} value={form.password} onChange={e => f('password', e.target.value)} type="password" />
        </Field>
        <Field label="Factoring Indicator">
          <input className={inp} value={form.factoring_ind} onChange={e => f('factoring_ind', e.target.value)} />
        </Field>
        <Field label="BP Project">
          <input className={inp} value={form.bp_project} onChange={e => f('bp_project', e.target.value)} />
        </Field>
      </div>

      <hr className="border-sap-border/50" />

      {/* Industry / business */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2">
        <Field label="Industry">
          <select className={sel} value={form.industry} onChange={e => f('industry', e.target.value)}>
            {INDUSTRY_OPTS.map(s => <option key={s} value={s}>{s || '— Select —'}</option>)}
          </select>
        </Field>
        <Field label="Type of Business">
          <select className={sel} value={form.type_of_business} onChange={e => f('type_of_business', e.target.value)}>
            {BUSINESS_TYPE_OPTS.map(s => <option key={s} value={s}>{s || '— Select —'}</option>)}
          </select>
        </Field>
        <Field label="Contact Person">
          <input className={inp} value={form.contact_person} onChange={e => f('contact_person', e.target.value)} />
        </Field>
        <Field label="ID No. 2">
          <input className={inp} value={form.id_no2} onChange={e => f('id_no2', e.target.value)} />
        </Field>

        <Field label="Unified Federal Tax ID">
          <input className={inp} value={form.unified_tax_id} onChange={e => f('unified_tax_id', e.target.value)} />
        </Field>
        <Field label="Remarks">
          <input className={inp} value={form.remarks_general} onChange={e => f('remarks_general', e.target.value)} />
        </Field>
        <Field label="BP Channel Code">
          <input className={inp} value={form.bp_channel_code} onChange={e => f('bp_channel_code', e.target.value)} />
        </Field>
        <div />
      </div>

      <hr className="border-sap-border/50" />

      {/* Territory / language / alias */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2">
        <Field label="Territory">
          <input className={inp} value={form.territory} onChange={e => f('territory', e.target.value)} />
        </Field>
        <Field label="Language">
          <select className={sel} value={form.language} onChange={e => f('language', e.target.value)}>
            {LANGUAGE_OPTS.map(s => <option key={s} value={s}>{s || '— Select —'}</option>)}
          </select>
        </Field>
        <Field label="Alias Name">
          <input className={inp} value={form.alias_name} onChange={e => f('alias_name', e.target.value)} />
        </Field>
        <Field label="GLN">
          <input className={inp} value={form.gln} onChange={e => f('gln', e.target.value)} />
        </Field>
      </div>

      <hr className="border-sap-border/50" />

      {/* Personal data protection & status */}
      <div className="flex items-center gap-6 text-xs">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={form.block_marketing}
            onChange={e => f('block_marketing', e.target.checked)}
            className="rounded border-sap-border text-sap-blue focus:ring-sap-blue/30" />
          <span className="text-sap-textLight">Block Sending Marketing Content</span>
        </label>
      </div>

      {/* Status radios */}
      <div className="flex items-center gap-6">
        <span className="text-[11px] font-medium text-sap-textLight">Status:</span>
        {STATUS_OPTS.map(s => (
          <label key={s} className="flex items-center gap-1.5 cursor-pointer text-xs">
            <input type="radio" name="vendor_status" value={s}
              checked={form.status === s}
              onChange={() => f('status', s)}
              className="text-sap-blue focus:ring-sap-blue/30" />
            <span className="text-sap-text">{s}</span>
          </label>
        ))}
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════
     TAB: Payment Terms
     ══════════════════════════════════════════════════ */
  const PaymentTermsTab = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2">
        <Field label="Payment Terms">
          <select className={sel} value={form.payment_terms} onChange={e => f('payment_terms', e.target.value)}>
            {PAYMENT_OPTS.map(s => <option key={s} value={s}>{s || '— Select —'}</option>)}
          </select>
        </Field>
        <Field label="Interest on Arrears %">
          <input className={inp} type="number" step="0.01" value={form.interest_on_arrears}
            onChange={e => f('interest_on_arrears', parseFloat(e.target.value) || 0)} />
        </Field>
        <Field label="Price List">
          <input className={inp} value={form.price_list} onChange={e => f('price_list', e.target.value)} />
        </Field>
        <Field label="Total Discount %">
          <input className={inp} type="number" step="0.01" value={form.total_discount}
            onChange={e => f('total_discount', parseFloat(e.target.value) || 0)} />
        </Field>

        <Field label="Credit Limit">
          <input className={inp} type="number" step="0.01" value={form.credit_limit}
            onChange={e => f('credit_limit', parseFloat(e.target.value) || 0)} />
        </Field>
        <Field label="Commitment Limit">
          <input className={inp} type="number" step="0.01" value={form.max_commitment}
            onChange={e => f('max_commitment', parseFloat(e.target.value) || 0)} />
        </Field>
        <Field label="Dunning Term">
          <select className={sel} value={form.dunning_term} onChange={e => f('dunning_term', e.target.value)}>
            {DUNNING_OPTS.map(s => <option key={s} value={s}>{s || '— Select —'}</option>)}
          </select>
        </Field>
        <Field label="Auto Posting">
          <select className={sel} value={form.auto_posting} onChange={e => f('auto_posting', e.target.value)}>
            {['No', 'Yes'].map(s => <option key={s}>{s}</option>)}
          </select>
        </Field>

        <Field label="Blanket Agreement">
          <input className={inp} value={form.blanket_agreement} onChange={e => f('blanket_agreement', e.target.value)} />
        </Field>
        <Field label="Effective Discount">
          <select className={sel} value={form.eff_discount_grp} onChange={e => f('eff_discount_grp', e.target.value)}>
            {['Lowest Discount', 'Highest Discount', 'Average', 'Discount Multiples'].map(s => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Effective Price">
          <select className={sel} value={form.eff_price} onChange={e => f('eff_price', e.target.value)}>
            {['Default Priority', 'Lowest Price', 'Highest Price'].map(s => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Average Delay (days)">
          <input className={inp} type="number" value={form.average_delay}
            onChange={e => f('average_delay', e.target.value)} />
        </Field>
      </div>

      <hr className="border-sap-border/50" />

      {/* Credit card */}
      <p className="text-[11px] font-semibold text-sap-text">Credit Card</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2">
        <Field label="Credit Card Type">
          <input className={inp} value={form.credit_card_type} onChange={e => f('credit_card_type', e.target.value)} />
        </Field>
        <Field label="Credit Card No.">
          <input className={inp} value={form.credit_card_no} onChange={e => f('credit_card_no', e.target.value)} />
        </Field>
        <Field label="Expiration Date">
          <input className={inp} value={form.cc_expiration} onChange={e => f('cc_expiration', e.target.value)} placeholder="MM/YY" />
        </Field>
        <Field label="ID Number">
          <input className={inp} value={form.cc_id_number} onChange={e => f('cc_id_number', e.target.value)} />
        </Field>
      </div>

      <hr className="border-sap-border/50" />

      {/* Bank details */}
      <p className="text-[11px] font-semibold text-sap-text">Bank Details</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2">
        <Field label="Country">
          <input className={inp} value={form.bank_country} onChange={e => f('bank_country', e.target.value)} />
        </Field>
        <Field label="Bank Name">
          <input className={inp} value={form.bank_name} onChange={e => f('bank_name', e.target.value)} />
        </Field>
        <Field label="Bank Code">
          <input className={inp} value={form.bank_code} onChange={e => f('bank_code', e.target.value)} />
        </Field>
        <Field label="Account No.">
          <input className={inp} value={form.bank_account} onChange={e => f('bank_account', e.target.value)} />
        </Field>
        <Field label="BIC / SWIFT">
          <input className={inp} value={form.bic_swift} onChange={e => f('bic_swift', e.target.value)} />
        </Field>
        <Field label="Account Name">
          <input className={inp} value={form.bank_account_name} onChange={e => f('bank_account_name', e.target.value)} />
        </Field>
        <Field label="Branch">
          <input className={inp} value={form.bank_branch} onChange={e => f('bank_branch', e.target.value)} />
        </Field>
        <Field label="Control ID">
          <input className={inp} value={form.bank_ctrl_id} onChange={e => f('bank_ctrl_id', e.target.value)} />
        </Field>
        <Field label="IBAN" span={2}>
          <input className={inp} value={form.bank_iban} onChange={e => f('bank_iban', e.target.value)} />
        </Field>
        <Field label="Mandate ID">
          <input className={inp} value={form.bank_mandate_id} onChange={e => f('bank_mandate_id', e.target.value)} />
        </Field>
        <Field label="Signature Date">
          <input className={inp} type="date" value={form.bank_sign_date} onChange={e => f('bank_sign_date', e.target.value)} />
        </Field>
      </div>

      <hr className="border-sap-border/50" />

      {/* Checkboxes */}
      <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs">
        {[
          ['allow_partial_delivery', 'Allow Partial Delivery'],
          ['allow_partial_per_row',  'Allow Partial Delivery per Row'],
          ['no_discount_groups',     'No Discount Groups'],
          ['endorsable_checks',      'Endorsable Checks / BoE'],
          ['accepts_endorsed',       'Accepts Endorsed Checks'],
        ].map(([key, label]) => (
          <label key={key} className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={!!form[key]}
              onChange={e => f(key, e.target.checked)}
              className="rounded border-sap-border text-sap-blue focus:ring-sap-blue/30" />
            <span className="text-sap-textLight">{label}</span>
          </label>
        ))}
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════
     TAB: Payment Run
     ══════════════════════════════════════════════════ */
  const PaymentRunTab = () => (
    <div className="space-y-4">
      <p className="text-[11px] font-semibold text-sap-text">House Bank</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2">
        <Field label="Country">
          <input className={inp} value={form.house_bank_country} onChange={e => f('house_bank_country', e.target.value)} />
        </Field>
        <Field label="Bank">
          <input className={inp} value={form.house_bank} onChange={e => f('house_bank', e.target.value)} />
        </Field>
        <Field label="Account">
          <input className={inp} value={form.house_bank_account} onChange={e => f('house_bank_account', e.target.value)} />
        </Field>
        <Field label="Branch">
          <input className={inp} value={form.house_bank_branch} onChange={e => f('house_bank_branch', e.target.value)} />
        </Field>
        <Field label="IBAN" span={2}>
          <input className={inp} value={form.house_bank_iban} onChange={e => f('house_bank_iban', e.target.value)} />
        </Field>
        <Field label="BIC / SWIFT Code">
          <input className={inp} value={form.house_bic_swift} onChange={e => f('house_bic_swift', e.target.value)} />
        </Field>
        <Field label="Control No.">
          <input className={inp} value={form.house_ctrl_no} onChange={e => f('house_ctrl_no', e.target.value)} />
        </Field>
      </div>

      <hr className="border-sap-border/50" />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2">
        <Field label="Reference Details" span={2}>
          <input className={inp} value={form.ref_details} onChange={e => f('ref_details', e.target.value)} />
        </Field>
        <Field label="Bank Charges Alloc. Code">
          <input className={inp} value={form.bank_charges_code} onChange={e => f('bank_charges_code', e.target.value)} />
        </Field>
        <div />
      </div>

      <hr className="border-sap-border/50" />

      <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs">
        {[
          ['payment_block',   'Payment Block'],
          ['single_payment',  'Single Payment'],
          ['collection_auth', 'Collection Authorization'],
        ].map(([key, label]) => (
          <label key={key} className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={!!form[key]}
              onChange={e => f(key, e.target.checked)}
              className="rounded border-sap-border text-sap-blue focus:ring-sap-blue/30" />
            <span className="text-sap-textLight">{label}</span>
          </label>
        ))}
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════
     TAB: Accounting
     ══════════════════════════════════════════════════ */
  const AccountingTab = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2">
        <Field label="Consolidating BP" span={2}>
          <input className={inp} value={form.consolidating_bp} onChange={e => f('consolidating_bp', e.target.value)} />
        </Field>
        <div />
        <div />

        <Field label="Accounts Receivable">
          <input className={inp} value={form.acct_receivable} onChange={e => f('acct_receivable', e.target.value)} />
        </Field>
        <Field label="Down Payment Clearing Acct">
          <input className={inp} value={form.dp_clearing_acct} onChange={e => f('dp_clearing_acct', e.target.value)} />
        </Field>
        <Field label="Down Payment Interim Acct">
          <input className={inp} value={form.dp_interim_acct} onChange={e => f('dp_interim_acct', e.target.value)} />
        </Field>
        <Field label="Planning Group">
          <input className={inp} value={form.planning_group} onChange={e => f('planning_group', e.target.value)} />
        </Field>
      </div>

      <hr className="border-sap-border/50" />

      <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs">
        {[
          ['payment_consolidation',  'Payment Consolidation'],
          ['delivery_consolidation', 'Delivery Consolidation'],
          ['affiliate',              'Affiliate'],
        ].map(([key, label]) => (
          <label key={key} className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={!!form[key]}
              onChange={e => f(key, e.target.checked)}
              className="rounded border-sap-border text-sap-blue focus:ring-sap-blue/30" />
            <span className="text-sap-textLight">{label}</span>
          </label>
        ))}
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════
     TAB: Remarks
     ══════════════════════════════════════════════════ */
  const RemarksTab = () => (
    <div>
      <textarea className="w-full h-52 p-3 text-xs border border-sap-border rounded bg-white resize-y
                           focus:outline-none focus:ring-1 focus:ring-sap-blue/40 focus:border-sap-blue"
        value={form.remarks} onChange={e => f('remarks', e.target.value)}
        placeholder="Enter remarks…" />
    </div>
  );

  /* ══════════════════════════════════════════════════
     RENDER
     ══════════════════════════════════════════════════ */
  return (
    <div className="space-y-3">
      {/* ── Title bar ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/purchasing/vendors')}
            className="p-1.5 rounded-lg hover:bg-sap-bg text-sap-textLight hover:text-sap-text transition-colors"
            title="Back to Vendors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
          <div>
            <h1 className="text-base font-bold text-sap-text">
              {isNew ? 'Business Partner Master Data' : `Vendor — ${form.vendor_code}`}
            </h1>
            <p className="text-2xs text-sap-textLight mt-0.5">
              {isNew ? 'Add new vendor' : form.vendor_name}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/purchasing/vendors')}
            className="px-4 py-2 text-xs font-medium border border-sap-border rounded-lg hover:bg-white transition-colors">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-5 py-2 text-xs font-semibold bg-sap-blue text-white rounded-lg
                       hover:bg-sap-blueDark disabled:opacity-40 shadow-sm transition-all">
            {saving ? 'Saving…' : isNew ? 'Add' : 'Update'}
          </button>
        </div>
      </div>

      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
      )}

      {/* ── Header fields (always visible, SAP 2-col style) ── */}
      <div className="bg-white rounded-xl shadow-sm border border-sap-border/40 p-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2">
          <Field label="Code">
            <input className={inp} value={form.vendor_code}
              onChange={e => f('vendor_code', e.target.value)}
              disabled={!isNew} />
          </Field>
          <Field label="Name" span={2}>
            <input className={inp} value={form.vendor_name}
              onChange={e => f('vendor_name', e.target.value)}
              placeholder="Vendor Name" />
          </Field>
          <Field label="Currency">
            <select className={sel} value={form.currency} onChange={e => f('currency', e.target.value)}>
              {CURRENCY_OPTS.map(c => <option key={c}>{c}</option>)}
            </select>
          </Field>

          <Field label="SAP Card Code (outbound override)" span={2}>
            <input className={inp} value={form.sap_card_code}
              onChange={e => f('sap_card_code', e.target.value)}
              placeholder="Leave blank to use Code above"
              title="When set, this CardCode is used instead of Code when pushing Purchase Orders to SAP B1. Use this when the SAP vendor CardCode differs from the local vendor code." />
          </Field>
          <Field label="Federal Tax ID">
            <input className={inp} value={form.federal_tax_id}
              onChange={e => f('federal_tax_id', e.target.value)} />
          </Field>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className="bg-white rounded-xl shadow-sm border border-sap-border/40 overflow-hidden">
        <div className="flex border-b border-sap-border/60 bg-[#F8FAFB] overflow-x-auto">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-5 py-2.5 text-xs font-semibold whitespace-nowrap transition-all border-b-2
                ${tab === t.key
                  ? 'border-sap-blue text-sap-blue bg-white'
                  : 'border-transparent text-sap-textLight hover:text-sap-text hover:bg-white/60'}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {tab === 'general'    && <GeneralTab />}
          {tab === 'payTerms'   && <PaymentTermsTab />}
          {tab === 'payRun'     && <PaymentRunTab />}
          {tab === 'accounting' && <AccountingTab />}
          {tab === 'remarks'    && <RemarksTab />}
        </div>
      </div>
    </div>
  );
}
